import { src, type DeviceKind, type Feature, type Shot } from '../catalog';
import { useLang } from '../lang';
import { SectionHead } from './Shared';

/** Feature blocks for a line's app, each paired with laptop and/or phone screens. */
export function Software({ prefix, features }: { prefix: string; features: readonly Feature[] }) {
  const { t } = useLang();
  return (
    <section className="band software" id="software" aria-labelledby={`${prefix}-title`}>
      <div className="frame">
        <SectionHead id={`${prefix}-title`} prefix={prefix} />
        <div className="soft-list">
          {features.map((f) => (
            <article key={f.id} className="soft-row" aria-labelledby={`${prefix}-${f.id}`}>
              <div className="soft-copy" data-reveal>
                <p className="mono soft-tag">
                  {t(`${prefix}.${f.id}.tag`)}
                  {f.status && f.status !== 'included' && (
                    <span className="soft-status">{t(`soft.${f.status}`)}</span>
                  )}
                </p>
                <h3 id={`${prefix}-${f.id}`} className="h3">
                  {t(`${prefix}.${f.id}.title`)}
                </h3>
                <p>{t(`${prefix}.${f.id}.body`)}</p>
                <ul className="ticks ticks-ink">
                  {[1, 2, 3].map((n) => (
                    <li key={n}>{t(`${prefix}.${f.id}.${n}`)}</li>
                  ))}
                </ul>
              </div>
              <div className={`stage stage-${layout(f)}`} data-reveal>
                {f.laptop && <Device shot={f.laptop} />}
                {f.phones.map((s) => (
                  <Device key={s.file} shot={s} />
                ))}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function layout(f: Feature): string {
  if (!f.laptop) return 'phones';
  return f.phones.length ? 'pair' : 'laptop';
}

const SIZES: Record<DeviceKind, string> = {
  laptop: '(min-width: 1024px) 58vw, 100vw',
  phone: '(min-width: 1024px) 16vw, 40vw',
};

export function Device({ shot, priority = false }: { shot: Shot; priority?: boolean }) {
  const { t } = useLang();
  return (
    <figure className={`device device-${shot.kind}`}>
      <div className="device-screen">
        <img
          src={src(shot)}
          srcSet={`${src(shot)} ${shot.w}w, ${import.meta.env.BASE_URL}${shot.file2x} ${shot.w * 2}w`}
          sizes={SIZES[shot.kind]}
          alt={t(shot.alt)}
          width={shot.w}
          height={shot.h}
          fetchPriority={priority ? 'high' : undefined}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
        />
      </div>
      {shot.kind === 'laptop' && <div className="device-base" aria-hidden="true" />}
    </figure>
  );
}
